import isNumber from 'lodash/isNumber';
import { arrayMove } from '@dnd-kit/sortable';
import * as ActionTypes from '../actions/kitchenPlayerAction';
import * as loginTypes from '../actions/loginAction';
import {
  getLocalStoragePlayers,
  prepareActivityData,
  setLocalStoragePlayers,
} from '../../containers/KitchenPlayer/utils/kitchenPlayer.utils';

export const DEFAULT_ACTIVITY_HISTORY_LIMIT = 20;

export function makePlayer(player) {
  const activeStreamId = player?.activeStreamId || 'select';
  const mute = activeStreamId !== 'select' ? Boolean(player?.mute) : false;
  
  const newPlayer = {
    activeStreamId,
    playStatus: 'play', // play, playing, pause, pausing
    restart: false,
    mute,
    showInfo: false,
    live: true,
    fullscreen: false,
    loading: false,
  };
  
  if (player?.plugLoading) {
    newPlayer.plugLoading = true;
  }
  
  return newPlayer;
}

export function makeActivityHistory(newActivityHistory) {
  let limit = newActivityHistory?.limit;
  
  if (!isNumber(limit) && limit !== null) {
    limit = DEFAULT_ACTIVITY_HISTORY_LIMIT;
  }
  
  return {
    data: newActivityHistory?.data || [],
    loading: Boolean(newActivityHistory?.loading),
    scrollLoading: false,
    cursorDirection: 'FIRST',
    cursor: null,
    existNext: false,
    existPrevious: false,
    limit,
    sortBy: 'eventTime',
    orderDirection: 'DESC',
    search: newActivityHistory?.search || '',
  };
}

const initialState = {
  startDate: null,
  endDate: null,
  timeInterval: null,
  problems: [],
  problem: '',
  problemMessage: '',
  another: '',
  attachmentFiles: [],
  attachments: [],
  isLoading: false,
  eventNames: [],
  activityHistory: makeActivityHistory(),
  players: [],
};

const kitchenPlayerReducer = (state = initialState, action) => {
  switch (action.type) {
    case ActionTypes.SET_ACTIVE_STREAMS: {
      const streamIds = action.payload.activeStreamIds;
      const restaurantUuid = action.payload.restaurantUuid;
      const prevPlayers = state.players;
      const lsPlayers = restaurantUuid ? getLocalStoragePlayers(restaurantUuid) : [];
      let newPlayers = [];
      
      for (const streamId of streamIds) {
        const prevPlayer = prevPlayers.find(item => item.activeStreamId === streamId);
        
        if (prevPlayer) {
          newPlayers.push(prevPlayer);
        } else {
          const lsPlayer = lsPlayers.find(item => item.activeStreamId === streamId);
          const player = { activeStreamId: streamId };
          
          if (lsPlayer) {
            player.mute = Boolean(lsPlayer.mute);
          }
          
          newPlayers.push(makePlayer(player));
        }
      }
      
      if (newPlayers.length > 1) {
        newPlayers = newPlayers.map(item => ({ ...item, live: true }));
      }
      
      if (restaurantUuid) {
        setLocalStoragePlayers(restaurantUuid, newPlayers);
      }
      
      return { ...state, players: newPlayers };
    }
  
    case ActionTypes.ADD_ACTIVE_STREAM: {
      const streamId = action.payload.activeStreamId;
      const restaurantUuid = action.payload.restaurantUuid;
      const hasPlayer = state.players.some(item => item.activeStreamId === streamId);
  
      if (!hasPlayer) {
        const lsPlayers = restaurantUuid ? getLocalStoragePlayers(restaurantUuid) : [];
        const lsPlayer = lsPlayers.find(item => item.activeStreamId === streamId);
        const player = { activeStreamId: streamId, mute: Boolean(lsPlayer?.mute) };
        let newPlayers = [...state.players, makePlayer(player)];
  
        if (newPlayers.length > 1) {
          newPlayers = newPlayers.map(item => ({ ...item, live: true }));
        }
  
        if (restaurantUuid) {
          setLocalStoragePlayers(restaurantUuid, newPlayers);
        }
        
        return { ...state, players: newPlayers };
      }
    
      return state;
    }
  
    case ActionTypes.REORDER_ACTIVE_STREAMS: {
      const { prevId, nextId, restaurantUuid } = action.payload;
      const prevIndex = state.players.findIndex(player => player.activeStreamId === prevId);
      const nextIndex = state.players.findIndex(player => player.activeStreamId === nextId);
  
      if (prevIndex < 0 || nextIndex < 0 || prevIndex === nextIndex) {
        return state;
      }
      
      const newPlayers = arrayMove(state.players, prevIndex, nextIndex);
  
      if (restaurantUuid) {
        setLocalStoragePlayers(restaurantUuid, newPlayers);
      }
    
      return { ...state, players: newPlayers };
    }
  
    case ActionTypes.REMOVE_ACTIVE_STREAM: {
      const activeStreamId = action.payload.activeStreamId;
      const restaurantUuid = action.payload.restaurantUuid;
      const newPlayers = state.players.filter(item => item.activeStreamId !== activeStreamId);
      
      if (restaurantUuid) {
        setLocalStoragePlayers(restaurantUuid, newPlayers);
      }
      
      return {
        ...state,
        players: newPlayers,
      };
    }

    case ActionTypes.CHANGE_PLAYER_SEVERAL_VALUES:
      return {
        ...state,
        ...action.payload,
      };

    case ActionTypes.SEND_SUPPORT:
      return {
        ...state,
        isLoading: true,
      };

    case ActionTypes.SEND_SUPPORT_RESULT:
    case ActionTypes.SEND_SUPPORT_ERROR:
      return {
        ...state,
        isLoading: false,
      };

    case ActionTypes.CHANGE_PLAYER_VALUE:
      return {
        ...state,
        [action.payload.key]: action.payload.value,
      };
  
    case ActionTypes.CHANGE_PLAYER_STREAM_VALUE: {
      const streamId = action.payload.streamId;
      
      const players = state.players.map(item => {
        if (item.activeStreamId === streamId) {
          return {
            ...item,
            [action.payload.key]: action.payload.value,
          };
        }
        
        return item;
      });
      
      return { ...state, players };
    }
  
    case ActionTypes.CHANGE_PLAYER_STREAM_SEVERAL_VALUES: {
      const streamId = action.payload.streamId;
    
      const players = state.players.map(item => {
        if (item.activeStreamId === streamId) {
          return {
            ...item,
            ...action.payload.values,
          };
        }
      
        return item;
      });
    
      return { ...state, players };
    }
  
    case ActionTypes.CHANGE_PLAYER_STREAM_MODE:
      const streamId = action.payload.streamId;
      const live = action.payload.live;
  
      const players = state.players.map(item => {
        if (item.activeStreamId === streamId) {
          return {
            ...item,
            live,
          };
        }
    
        return item;
      });
  
      return {
        ...state,
        players,
        activityHistory: makeActivityHistory({
          limit: live ? DEFAULT_ACTIVITY_HISTORY_LIMIT : 5000,
          search: state.activityHistory.search,
        }),
      };
    
    case ActionTypes.CHANGE_ACTIVITY_HISTORY_SEARCH: {
      return {
        ...state,
        activityHistory: makeActivityHistory({
          search: action.payload.search,
          limit: state.activityHistory.limit,
        }),
      };
    }

    case ActionTypes.GET_SUPPORT:
      return {
        ...state,
        isLoading: true,
      };

    case ActionTypes.GET_SUPPORT_RESULT:
      return {
        ...state,
        problems: action.payload,
        isLoading: false,
      };

    case ActionTypes.GET_SUPPORT_ERROR:
      return {
        ...state,
        isLoading: false,
        isError: action.payload,
      };

    case ActionTypes.ADD_SUPPORT_ATTACHMENT:
      return {
        ...state,
        attachments: [action.payload.file, ...state.attachments],
      };

    case ActionTypes.EDIT_SUPPORT_ATTACHMENT:
      return {
        ...state,
        attachments: state.attachments.map((attachment) => {
          if (attachment.id === action.payload.id) {
            return {
              ...attachment,
              [action.payload.key]: action.payload.value,
            };
          } else {
            return attachment;
          }
        }),
      };

    case ActionTypes.DELETE_SUPPORT_ATTACHMENT:
      return {
        ...state,
        attachments: state.attachments.filter((attachment) => attachment.id !== action.payload.id),
      };

    case ActionTypes.GET_STREAM_ACTIVITY:
      return {
        ...state,
        activityHistory: {
          ...state.activityHistory,
          data: action.payload ? [] : state.activityHistory.data,
          loading: action.payload,
          scrollLoading: !action.payload,
        },
      };

    case ActionTypes.GET_STREAM_ACTIVITY_SUCCESS:
      return {
        ...state,
        activityHistory:
          state.eventNames.length > 0
            ? {
                ...state.activityHistory,
                loading: false,
                scrollLoading: false,
                cursor: action.payload.cursor,
                cursorDirection: action.payload.cursorDirection,
                existNext: action.payload.existsNextPage,
                existPrevious: action.payload.existsPreviousPage,
                data:
                  action.params.cursorDirection === 'NEXT'
                    ? prepareActivityData([...state.activityHistory.data, ...action.payload.content], action.timezone)
                    : prepareActivityData(action.payload.content, action.timezone),
              }
            : { ...state.activityHistory },
      };

    case ActionTypes.ADD_STREAM_ACTIVITY_EVENT:
      return {
        ...state,
        activityHistory: {
          ...state.activityHistory,
          data: prepareActivityData([action.payload, ...state.activityHistory.data], action.timezone),
        },
      };
  
    case ActionTypes.ADD_STREAM_ACTIVITY_EVENTS:
      return {
        ...state,
        activityHistory: {
          ...state.activityHistory,
          data: prepareActivityData([...action.payload, ...state.activityHistory.data], action.timezone),
        },
      };

    case ActionTypes.GET_STREAM_ACTIVITY_ERROR:
      return {
        ...state,
        activityHistory: {
          ...state.activityHistory,
          loading: false,
          scrollLoading: false,
        },
      };
  
    case ActionTypes.SET_STREAM_ACTIVITY_LOADING:
      return {
        ...state,
        activityHistory: {
          ...state.activityHistory,
          loading: Boolean(action.payload),
        },
      };
  
    case loginTypes.CHANGE_RESTAURANT_UUID:
      return {
        ...state,
        players: [],
      };

    case ActionTypes.RESET_STREAM_ACTIVITY:
      return {
        ...state,
        activityHistory: makeActivityHistory({
          search: state.activityHistory.search,
          limit: state.activityHistory.limit,
          loading: action.payload?.loading,
        }),
      };

    case ActionTypes.RESET_KITCHEN_PLAYER_STORE:
      return {
        ...state,
        ...initialState,
      };

    default:
      return state;
  }
};

export default kitchenPlayerReducer;
